import React, { useEffect, useState, useRef } from 'react';
import { Clock, Hourglass } from 'lucide-react';

/**
 * Live countdown to a delivery request's `expires_at` ISO timestamp.
 *
 * Two display modes:
 *   - variant="customer" (default) → live HH:MM:SS, red urgency theme.
 *     Always shown above the driver bids on the customer side.
 *   - variant="driver" → soft "Request closes in 22 hours" style message,
 *     no seconds, no pressure language, calm grey theme.
 *
 * Auto-updates every second. Calls onExpire() once when the timer hits zero.
 */
export const ExpiryCountdown = ({ expiresAt, variant = 'customer', onExpire }) => {
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const target = expiresAt ? new Date(expiresAt).getTime() : null;
  const diffMs = target != null ? target - now : 0;
  const isExpired = expiresAt && diffMs <= 0;

  useEffect(() => {
    if (isExpired && !firedRef.current && typeof onExpire === 'function') {
      firedRef.current = true;
      onExpire();
    }
  }, [isExpired, onExpire]);

  if (!expiresAt) return null;

  if (isExpired) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs sm:text-sm border ${variant === 'customer' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
        data-testid="expiry-countdown-expired"
      >
        <Hourglass className="w-4 h-4 flex-shrink-0" />
        <span className="font-medium">Request expired</span>
      </div>
    );
  }

  const totalSec = Math.floor(diffMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');

  if (variant === 'driver') {
    // Driver-side: soft, calm, no seconds, no pressure.
    const hours = Math.max(1, Math.round(totalSec / 3600));
    const hoursLabel = hours === 1 ? 'hour' : 'hours';
    return (
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200 text-xs text-gray-600"
        data-testid="expiry-driver-soft"
      >
        <Clock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
        <span>
          <span aria-hidden="true">⏰ </span>
          Request closes in <span className="font-semibold text-gray-700">{hours} {hoursLabel}</span>.
          Drivers may still place bids.
        </span>
      </div>
    );
  }

  // Customer-side: live HH:MM:SS, red urgency theme.
  const sameDayLikely = totalSec < 4 * 3600;
  const label = sameDayLikely
    ? `Expires in ${pad(h)}:${pad(m)}:${pad(s)} if no driver is selected.`
    : `Request expires in ${pad(h)}:${pad(m)}:${pad(s)} if no driver is selected.`;
  return (
    <div
      className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-200 text-red-700"
      data-testid="expiry-customer-countdown"
      role="timer"
      aria-live="polite"
    >
      <Hourglass
        className="w-4 h-4 text-red-600 flex-shrink-0 animate-spin"
        style={{ animationDuration: '3s' }}
        aria-hidden="true"
      />
      <div className="text-sm font-semibold leading-snug">
        <span className="tabular-nums" data-testid="expiry-countdown-value">{label}</span>
      </div>
    </div>
  );
};

export default ExpiryCountdown;
